import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, FileText, FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MyDocument, MyDocumentList } from '@up2d8/contracts';
import { Badge, Button, Card, EmptyState, LoadingState, useToast } from '@up2d8/ui';
import { DeskShell } from './desk/desk-shell';
import { useSession } from '../../shared/session';
import { safeNavigationUrl } from '../../shared/safe-url';
import { ListError } from '../../shared/list-error';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';

/**
 * De documenten van de medewerker (Stap 20).
 *
 * Wat te bevestigen staat, staat bovenaan. Dat is geen vormkeuze: de rest van deze lijst is een
 * naslagwerk dat je opzoekt, en dit ene is iets wat van jou gevraagd wordt. Zonder die scheiding
 * staat de veiligheidsinstructie die vandaag gelezen moet worden tussen zeven handleidingen van
 * vorig jaar.
 *
 * Er is geen versiegeschiedenis op dit scherm, en dat is bewust: de medewerker ziet uitsluitend de
 * geldige versie. Een vervallen werkinstructie openen is precies wat dit systeem hoort te
 * voorkomen, en de goedkoopste garantie is dat de oude versies niet in het antwoord zitten.
 */
export function AppDocumentsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const tabs = useAppTabs();

  const documents = useQuery({
    queryKey: ['my-documents'],
    queryFn: async (): Promise<MyDocument[]> => {
      const { data, error } = await api.GET('/api/v1/me/documents');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as MyDocumentList).items;
    },
  });

  const items = documents.data ?? [];
  const todo = items.filter((item) => item.requiresAcknowledgement && !item.acknowledgedAt);
  const rest = items.filter((item) => !todo.includes(item));

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="documents"
      renderLink={renderAppTabLink}
    >
      <h1 className="font-heading text-page-title font-semibold text-text-primary">
        {t('documents.title')}
      </h1>

      {documents.isPending ? (
        <LoadingState rows={3} label={t('common.loading')} />
      ) : documents.isError ? (
        <ListError onRetry={() => void documents.refetch()} error={documents.error} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState title={t('documents.empty')} description={t('documents.emptyHint')} />
        </Card>
      ) : (
        <div className="flex flex-col gap-section">
          {todo.length > 0 && (
            <section className="flex flex-col gap-element">
              <h2 className="font-heading text-small font-semibold uppercase tracking-wide text-brand-primary">
                {t('documents.toRead', { count: todo.length })}
              </h2>
              {todo.map((item) => (
                <DocumentCard key={item.versionId} item={item} />
              ))}
            </section>
          )}

          {rest.length > 0 && (
            <section className="flex flex-col gap-element">
              <h2 className="font-heading text-small font-semibold uppercase tracking-wide text-text-muted">
                {t('documents.library')}
              </h2>
              {rest.map((item) => (
                <DocumentCard key={item.versionId} item={item} />
              ))}
            </section>
          )}
        </div>
      )}
    </DeskShell>
  );
}

function DocumentCard({ item }: { item: MyDocument }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  /**
   * Het bestand openen.
   *
   * De download-URL wordt pas bij het tikken opgehaald en is kortlevend (ADR-0015), dus hij staat
   * niet in de lijstquery. Het tabblad wordt synchroon geopend en pas daarna gevuld: een
   * `window.open` ná een `await` wordt door elke mobiele browser als pop-up geblokkeerd, omdat de
   * tik dan niet meer "de oorzaak" is.
   */
  const open = useMutation({
    mutationFn: async () => {
      const tab = window.open('', '_blank');
      const { data, error } = await api.GET('/api/v1/media/{fileId}/download', {
        params: { path: { fileId: item.fileId } },
      });
      const url = data ? safeNavigationUrl((data as { url: string }).url) : null;
      if (error || !url) {
        tab?.close();
        throw error ?? new Error('Openen mislukt');
      }
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      }
    },
    onError: () => toast.show({ tone: 'danger', title: t('documents.openFailed') }),
  });

  const acknowledge = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/me/documents/versions/{versionId}/acknowledge', {
        params: { path: { versionId: item.versionId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      // Geen optimistische update: een kennisname is bewijs, en dan hoort de medewerker te zien
      // dat de server het ook echt heeft vastgelegd.
      void queryClient.invalidateQueries({ queryKey: ['my-documents'] });
      void queryClient.invalidateQueries({ queryKey: ['open-items'] });
      toast.show({ tone: 'success', title: t('documents.acknowledged') });
    },
    onError: () => toast.show({ tone: 'danger', title: t('documents.acknowledgeFailed') }),
  });

  const mustRead = item.requiresAcknowledgement && !item.acknowledgedAt;

  return (
    <Card data-testid="document-card">
      <div className="flex items-start justify-between gap-element">
        <div className="min-w-0">
          {item.folderName && (
            <p className="flex items-center gap-1.5 text-small text-text-muted">
              <FolderOpen aria-hidden className="h-3.5 w-3.5 shrink-0" />
              {item.folderName}
            </p>
          )}
          <h3 className="mt-0.5 font-heading text-title-md font-semibold text-text-primary">
            {item.title}
          </h3>
          <p className="mt-0.5 text-small text-text-secondary">
            {t('documents.version', { label: item.versionLabel })}
          </p>
        </div>
        {item.acknowledgedAt ? (
          <Badge tone="success">
            <CheckCircle2 aria-hidden className="mr-1 inline h-3.5 w-3.5" />
            {t('documents.read')}
          </Badge>
        ) : mustRead ? (
          <Badge tone="warning">{t('documents.mustRead')}</Badge>
        ) : null}
      </div>

      {item.changeNote && (
        <p className="mt-element text-content text-text-secondary">
          <span className="font-semibold">{t('documents.whatChanged')}</span> {item.changeNote}
        </p>
      )}

      <div className="mt-element flex flex-wrap gap-2 border-t border-border-light pt-element">
        <Button
          variant="secondary"
          data-testid="document-open"
          loading={open.isPending}
          onClick={() => open.mutate()}
        >
          <FileText aria-hidden className="mr-1 inline h-4 w-4" />
          {t('documents.open')}
          <ExternalLink aria-hidden className="ml-1 inline h-3.5 w-3.5" />
        </Button>
        {mustRead && (
          <Button
            data-testid="document-acknowledge"
            loading={acknowledge.isPending}
            onClick={() => acknowledge.mutate()}
          >
            {t('documents.acknowledge')}
          </Button>
        )}
      </div>
    </Card>
  );
}
